import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { useStore } from "../../../contexts/store-provider";
import { ICategoryProduct } from "../../../models/state/IStoreProduct";
import { formatMessage } from "../../../translations/format-message";
import ProductRow from "./product-row";

export interface CategoryItemProductProps {
  category: ICategoryProduct;
  index: number;
  parentId: string | null;
  onDetailClick: (product: any) => void;
  setCategories: any;
  storeId: number;
  openAccordions: Set<string>;
  setOpenAccordions: React.Dispatch<React.SetStateAction<Set<string>>>;
  isDisabled: boolean;
  selectedProduct: any;
  saleItemIds: number[];
  setSelectedProduct: any;
}
// Recursive component that handles category and subcategory rendering
const CategoryProductItems: React.FC<CategoryItemProductProps> = ({
  category,
  index,
  parentId,
  onDetailClick,
  setCategories,
  storeId,
  openAccordions,
  setOpenAccordions,
  isDisabled,
  selectedProduct,
  saleItemIds,
  setSelectedProduct,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { productManagementStore } = useStore();
  const {
    GetProductListByCatIdService,
    getProductData,
    addUpdateStoreProductOrderState,
    resetAddUpdateStoreProductOrderState,
  } = productManagementStore;
  const [productArray, setProductArray] = useState<any[]>([]);
  const [accordionEventKey, setAccordionEventKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const currentEventKey = `${parentId || "root"}-${index}`;
  const isAccordionOpen = openAccordions.has(currentEventKey);
  // Fetch products when this category's accordion is opened
  useEffect(() => {
    const eventKey = `${parentId || "root"}-${index}`;
    if (openAccordions.has(eventKey) && category.categoryId) {
      const fetchProductsForCategory = async () => {
        setIsLoading(true);
        try {
          await GetProductListByCatIdService(
            Number(category.categoryId),
            storeId,
            true
          );
          const products =
            getProductData.get(Number(category.categoryId)) || [];
          setProductArray(products);

          // Update the category with products in the parent state
          setCategories((prevCategories: ICategoryProduct[]) => {
            const updateCategoryWithProducts = (
              categories: ICategoryProduct[]
            ): ICategoryProduct[] => {
              return categories.map((cat) => {
                if (cat.categoryId === category.categoryId) {
                  return { ...cat, products };
                }
                if (cat.subCategories.length > 0) {
                  return {
                    ...cat,
                    subCategories: updateCategoryWithProducts(
                      cat.subCategories
                    ),
                  };
                }
                return cat;
              });
            };
            return updateCategoryWithProducts([...prevCategories]);
          });
        } catch (error) {
          console.error(
            `Error fetching products for category ${category.categoryId}:`,
            error
          );
        } finally {
          setIsLoading(false);
        }
      };

      fetchProductsForCategory();
    }
  }, [
    category.categoryId,
    GetProductListByCatIdService,
    getProductData,
    index,
    openAccordions,
    parentId,
    setCategories,
    storeId,
  ]);

  // Handle product order update success - This is the key function that refreshes products after reordering
  useEffect(() => {
    if (isAccordionOpen && addUpdateStoreProductOrderState.success) {
      setIsLoading(true); // Show loading state
      const fetchProductsAfterReorder = async () => {
        try {
          // Fetch updated products regardless of accordionEventKey
          await GetProductListByCatIdService(
            Number(category.categoryId),
            storeId,
            true
          );
          const products =
            getProductData.get(Number(category.categoryId)) || [];
          setProductArray(products);

          // Update the main categories state
          setCategories((prevCategories: ICategoryProduct[]) => {
            const updateCategoryWithProducts = (
              categories: ICategoryProduct[]
            ): ICategoryProduct[] => {
              return categories.map((cat) => {
                if (cat.categoryId === category.categoryId) {
                  return { ...cat, products };
                }
                if (cat.subCategories.length > 0) {
                  return {
                    ...cat,
                    subCategories: updateCategoryWithProducts(
                      cat.subCategories
                    ),
                  };
                }
                return cat;
              });
            };
            return updateCategoryWithProducts([...prevCategories]);
          });
        } catch (error) {
          console.error("Error fetching products after reorder:", error);
        } finally {
          setIsLoading(false);
          resetAddUpdateStoreProductOrderState(); // Important: reset state after update
        }
      };

      fetchProductsAfterReorder();
    }
  }, [
    addUpdateStoreProductOrderState.success,
    category.categoryId,
    GetProductListByCatIdService,
    getProductData,
    resetAddUpdateStoreProductOrderState,
    setCategories,
    storeId,
    isAccordionOpen,
  ]);

  // Track accordion open/close state
  const handleAccordionClick = () => {
    const newEventKey = `${parentId || "root"}-${index}`;
    const newOpenAccordions = new Set(openAccordions);

    if (newOpenAccordions.has(newEventKey)) {
      newOpenAccordions.delete(newEventKey);
      setAccordionEventKey("");
    } else {
      newOpenAccordions.add(newEventKey);
      setAccordionEventKey(newEventKey);
    }

    setOpenAccordions(newOpenAccordions);
  };

  return (
    <div className="service-selection">
      <Accordion.Item key={category.categoryId} eventKey={currentEventKey}>
        <Accordion.Header onClick={handleAccordionClick}>
            <span className="flex-grow-1">{category.name}</span>
            {isAccordionOpen && isLoading && (
              <span
                className="ms-2 spinner-border spinner-border-sm text-primary"
                role="status"
                aria-hidden="true"
              ></span>
            )}
        </Accordion.Header>
        <Accordion.Body className="px-1 py-0 mt-2">
          {/* Render products if available */}
          {(category.products && category.products.length > 0) ? (
              <>
                {category.products.map((product, index) => (
                  <ProductRow
                    key={`product-${product.Id}-${category.categoryId}`}
                    id={`product-${product.Id}-${category.categoryId}`}
                    product={product}
                    index={index}
                    categoryId={category.categoryId}
                    onDetailClick={onDetailClick}
                    isDisabled={isDisabled}
                    selectedProduct={selectedProduct}
                    saleItemIds={saleItemIds}
                    setSelectedProduct={setSelectedProduct}
                  />
                ))}
              </>
          ): (isAccordionOpen && !isLoading && category.subCategories.length == 0 ) ? (
            <div className="mb-2">{formatMessage("no_product_found_in_this_category")}</div>
          ) : null}
          {/* Recursively render subcategories */}
          {category.subCategories.length > 0 && (
            <Accordion alwaysOpen className="mt-2">
              <div className="subcategories-container">
                {category.subCategories.map((subCategory, subIndex) => (
                  <CategoryProductItems
                    key={subCategory.categoryId}
                    category={subCategory}
                    index={subIndex}
                    parentId={category.categoryId}
                    onDetailClick={onDetailClick}
                    setCategories={setCategories}
                    storeId={storeId}
                    openAccordions={openAccordions}
                    setOpenAccordions={setOpenAccordions}
                    isDisabled={isDisabled}
                    selectedProduct={selectedProduct}
                    saleItemIds={saleItemIds}
                    setSelectedProduct={setSelectedProduct}
                  />
                ))}
              </div>
            </Accordion>
          )}
        </Accordion.Body>
      </Accordion.Item>
    </div>
  );
};
export default observer(CategoryProductItems);
